// TODO This is workaround for https://github.com/microsoft/TypeScript/issues/47663#issuecomment-1694517404
export * from '@tanstack/query-core';

import { useCallback, useMemo } from 'react';

import type { RhinoImageRatio } from '../image';

import { useCollection } from './useCollection';
import { useCollectionMeta } from './useCollectionMeta';

export type UseCollectionProps = {
  gridReferenceId: string;
  railReferenceId: string;
  pageSize: number;
  defaultPortraitImageHeight?: number;
  defaultLandscapeImageHeight?: number;
  defaultImageRatio?: RhinoImageRatio;
};

function createMeta(meta: NonNullable<ReturnType<typeof useCollectionMeta>['data']>) {
  return {
    collectionId: meta.collectionId,
    groupBy: meta.groupBy,
    isFilterByEntitlementRequired: meta.isFilterByEntitlementRequired,
    kSql: meta.kSql,
  };
}

export function useGridCollection({
  gridReferenceId,
  railReferenceId,
  pageSize,
  defaultPortraitImageHeight,
  defaultLandscapeImageHeight,
  defaultImageRatio,
}: UseCollectionProps) {
  const {
    data: meta,
    error: collectionMetaError,
    isLoading: isCollectionMetaFetching,
    refetch: refetchCollectionMeta,
  } = useCollectionMeta({
    gridReferenceId,
    railReferenceId,
    defaultPortraitImageHeight,
    defaultLandscapeImageHeight,
    defaultImageRatio,
  });

  const { refetch: refetchCollection, ...collectionData } = useCollection({
    meta: meta ? createMeta(meta) : undefined,
    pageSize,
    referenceId: railReferenceId,
  });

  const refetch = useCallback(async () => {
    await Promise.all([refetchCollection(), refetchCollectionMeta()]);
  }, [refetchCollection, refetchCollectionMeta]);

  return useMemo(() => {
    const error = collectionMetaError ?? collectionData.error;
    const isLoading = (isCollectionMetaFetching || collectionData.isLoading) && !error;

    return {
      ...collectionData,
      error,
      isLoading,
      refetch,
    };
  }, [collectionData, collectionMetaError, isCollectionMetaFetching, refetch]);
}
